/**
 * A partner's proposal, at its own link.
 *
 * Public and unauthenticated on purpose: someone evaluating an offer should not have to
 * remember a password to look at it, and the link is the same artefact whether it arrives by
 * email, WhatsApp, or a rep pasting it into a chat. The token in the URL is the capability.
 *
 * This file is now only the SHELL — the fetch, the page chrome, and what happens after accept.
 * The document itself lives in components/proposal/ProposalContent.jsx, because a partner who
 * is already signed in reads the same thing in a dialog inside the portal and neither copy
 * should be allowed to drift from the other.
 *
 * ## What it deliberately is not
 *
 * It is not a quote. It said "you will earn ₹X a month" — the ladder multiplied by a client
 * count someone read off a call — and a book changes every month, so the page promised what it
 * could not keep. Then it still ASKED for that client count in order to generate, which is the
 * same mistake one layer down. Neither is here now. Nothing on this page describes their
 * business, and nothing about it changes if their business changes.
 *
 * Numbers that move belong in the calculator, which is built for exactly that.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ProposalContent, ProposalLoading } from '../components/proposal/ProposalContent';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProposalPage = () => {
  const { token } = useParams();
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deciding, setDeciding] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/proposals/${token}`);
      setProposal(res.data);
    } catch (e) {
      setError(e.response?.status === 404
        ? 'This proposal link is not valid. Ask your contact at Expedify for a new one.'
        : 'Could not load this proposal.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const accept = async () => {
    setDeciding(true);
    try {
      await axios.post(`${API_URL}/api/proposals/${token}/accept`);
      toast.success('Accepted — we will be in touch to get you set up.');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not record that.');
    } finally {
      setDeciding(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-[#FAFAF8]"><ProposalLoading /></div>;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center px-4">
        <p className="text-[#64748B] text-center max-w-md">{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF8] py-10 px-4" data-testid="proposal-page">
      <div className="max-w-3xl mx-auto space-y-6">
        <div>
          <p className="text-xs uppercase tracking-wider text-[#64748B]">Expedify partnership</p>
          <h1 className="text-3xl font-bold text-[#1A1A1A] mt-1">
            {proposal.company_name || proposal.partner_name}
          </h1>
          <p className="text-[#64748B] mt-2">
            {/* The reference identifies the DOCUMENT, not the price list — it belongs beside
                "proposal", not glued to "rate card", which read as a rate card called
                P-202608-0010. The internal card revision is deliberately not shown at all. */}
            Every way to work with us and what each pays. Accepting pins these rates to your
            partnership — if we publish a new card, yours does not move.
          </p>
        </div>

        <ProposalContent proposal={proposal} onAccept={accept} deciding={deciding} />

        {/* Anyone reading this while signed in can get back to where they were. The link is
            plain rather than a button: most people arriving here came from an email and have
            no portal session, and a prominent "Go to portal" would send them to a login. */}
        <p className="text-xs text-[#64748B] text-center pb-6">
          <a href="/dashboard" className="hover:underline">Go to your partner portal →</a>
        </p>
      </div>
    </div>
  );
};

export default ProposalPage;
